import { messages, searchNotFound } from "../../lib/messages";
import { ProductCard } from "./ProductCard";
import { useLoadMoreOnVisible } from "./useLoadMoreOnVisible";
import { useProducts } from "./useProducts";

interface ProductGridProps {
  categoryId: string | undefined;
  search: string;
  managerUrl: string;
  onOpenProduct(productId: string): void;
  onClearSearch(): void;
}

/**
 * ProductGrid — сетка товаров каталога с бесконечным скроллом и пустыми состояниями
 * (docs/07-miniapp-ux.md §2.2). Загрузку/ошибку первой страницы решает CatalogPage —
 * этот компонент рендерится, когда есть с чем работать TanStack Query.
 */
export function ProductGrid({ categoryId, search, managerUrl, onOpenProduct, onClearSearch }: ProductGridProps) {
  const products = useProducts({ categoryId, search: search || undefined });
  const loadMoreRef = useLoadMoreOnVisible(
    () => {
      if (!products.isFetchingNextPage) {
        void products.fetchNextPage();
      }
    },
    Boolean(products.hasNextPage),
  );

  if (products.isPending) {
    return <GridSkeleton />;
  }

  if (products.isError) {
    return <p className="p-4 text-center text-hint">{messages.requestFailed}</p>;
  }

  const items = products.data.pages.flatMap((page) => page.items);

  if (items.length === 0 && search) {
    return (
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <p className="text-text">{searchNotFound(search)}</p>
        <button type="button" onClick={onClearSearch} className="rounded-card bg-button px-4 py-2 text-button-text">
          {messages.showAllProducts}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <p className="text-text">{messages.emptyCatalogTitle}</p>
        {managerUrl ? (
          <a href={managerUrl} target="_blank" rel="noreferrer" className="rounded-card bg-button px-4 py-2 text-button-text">
            {messages.writeToManager}
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 px-4">
        {items.map((product) => (
          <ProductCard key={product.id} product={product} onOpen={onOpenProduct} />
        ))}
      </div>
      <div ref={loadMoreRef} aria-hidden="true" className="h-1" />
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 px-4">
      <div className="h-52 animate-pulse rounded-card bg-surface" />
      <div className="h-52 animate-pulse rounded-card bg-surface" />
    </div>
  );
}
